/**
 * La cadena **contratado → instalado → activo**, que es la aportación central
 * del módulo.
 *
 * Clientes ya publica los dos primeros tramos. Lo que agrega esta barra es el
 * tercero, y sobre todo el segmento del medio: los empleados digitales que están
 * instalados y **no producen**, que hasta ahora contaban como éxito en la brecha
 * de activación.
 *
 * Los tres segmentos suman los contratados, así que los tres porcentajes se leen
 * sobre la misma base y la leyenda de `ShareBar` los imprime con su cantidad al
 * lado. Los porcentajes de la cadena (`pctInstalado`,
 * `pctActivoSobreInstalados`, `pctActivoSobreContratados`) vienen calculados del
 * contrato y se repiten en prosa con sus dos términos, no pelados.
 *
 * **El texto del medio no puede contradecir al bloque de abajo.** `brechaUso`
 * cuenta 19 y la lista de "agentes sin uso" muestra 14: no es un error, son 14
 * con cero trazas medidas más 5 que nadie pudo medir. La bajada lo dice, porque
 * una barra que afirma 19 al lado de una tabla que muestra 14 es exactamente el
 * tipo de contradicción que la regla 7 prohíbe.
 */
import type { CadenaActivacion } from '@ops/contracts'
import { formatMetricValue } from '@ops/contracts'

import { plural } from '../../lib/format'
import { Panel, ShareBar } from '../../ui'
import { Vacio } from '../comunes'
import { segmentosDeCadena } from './derivar'

export interface CadenaBlockProps {
  cadena: CadenaActivacion | null
  /** Instalados con cero trazas **medidas**. */
  conCeroTrazas: number
  /** Instalados que no se pueden medir por falta de prefijo de sesión. */
  sinMedicion: number
}

export function CadenaBlock({
  cadena,
  conCeroTrazas,
  sinMedicion,
}: CadenaBlockProps) {
  const segmentos = segmentosDeCadena(cadena)

  if (cadena === null || segmentos === null) {
    return (
      <Panel titulo="Cadena de activación" padding="sm">
        <Vacio
          icono={null}
          punteada
          mensaje="Todavía no hay empleados digitales contratados cargados"
          detalle="La cadena se cuenta sobre la tabla de agentes de la plataforma: sin filas no hay tramos que medir."
        />
      </Panel>
    )
  }

  const pctInstalado =
    cadena.pctInstalado === null
      ? ''
      : ` (${formatMetricValue(cadena.pctInstalado, 'percent')})`
  const sobreInstalados =
    cadena.pctActivoSobreInstalados === null
      ? ''
      : ` (${formatMetricValue(cadena.pctActivoSobreInstalados, 'percent')} de los instalados`
  const sobreContratados =
    cadena.pctActivoSobreContratados === null
      ? ''
      : `${sobreInstalados === '' ? ' (' : ', '}${formatMetricValue(cadena.pctActivoSobreContratados, 'percent')} de los contratados)`

  return (
    <Panel
      titulo="Cadena de activación"
      subtitulo={
        <>
          De <b>{formatMetricValue(cadena.contratados, 'count')}</b> empleados
          digitales contratados hay{' '}
          <b>{formatMetricValue(cadena.instalados, 'count')}</b> instalados
          {/* Con contratados > 0 —lo garantiza `segmentosDeCadena`— los dos
              porcentajes sobre contratados existen. El de "sobre instalados" no,
              cuando no hay ninguno instalado: ahí la frase se corta en vez de
              imprimir un guion. */}
          {pctInstalado} y <b>{formatMetricValue(cadena.activos, 'count')}</b> con
          al menos una traza en el período{sobreInstalados}
          {sobreContratados}.
        </>
      }
      padding="sm"
      footer={
        <>
          Los <b>{formatMetricValue(cadena.brechaUso, 'count')}</b> instalados sin
          uso son <b>{formatMetricValue(conCeroTrazas, 'count')}</b> con cero
          trazas medidas y <b>{formatMetricValue(sinMedicion, 'count')}</b> que no
          se {plural(sinMedicion, 'puede', 'pueden')} medir. Son dos hechos
          distintos y van en dos bloques distintos: decir "cero trazas" de un
          agente que nadie midió sería inventar.
        </>
      }
    >
      <ShareBar
        segmentos={segmentos}
        label="Cadena de activación: contratado, instalado, activo"
        total={cadena.contratados}
        unidad="count"
      />
    </Panel>
  )
}
